import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type {
  ClassDependants,
  AssetClassRecord,
  AssetModelRecord,
  MergeModelsBody,
  AssignmentGroupRecord,
  AttributeDefRecord,
  ChangeTemplateRecord,
  CommonsImportEntity,
  CommonsImportReport,
  KbArticleRecord,
  KpiBenchmarkRecord,
  LocationTokenRecord,
  PmCatalogRecord,
  PriorityEntry,
  SlaPolicy,
  RoutingRuleRecord,
  OnCallShiftRecord,
} from './itsm-commons.types';

const BASE = '/users/v1/itsm/commons';
const KEY = ['itsm-commons'] as const;

/** An asked refresh of everything this page has open — the active tab's queries, nothing else. */
export function useRefreshItsmCommons() {
  const qc = useQueryClient();
  return () => qc.refetchQueries({ queryKey: KEY, type: 'active' });
}

export function usePriorityMatrix() {
  return useQuery({
    queryKey: [...KEY, 'matrix'],
    queryFn: () => api.get<PriorityEntry[]>(`${BASE}/priority-matrix`),
  });
}

export function useSetPriority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PriorityEntry) => api.post<PriorityEntry>(`${BASE}/priority-matrix`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSlaPolicies() {
  return useQuery({
    queryKey: [...KEY, 'sla'],
    queryFn: () => api.get<SlaPolicy[]>(`${BASE}/sla`),
  });
}

export function useSetSlaPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SlaPolicy) => api.post<SlaPolicy>(`${BASE}/sla`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAssignmentGroups() {
  return useQuery({
    queryKey: [...KEY, 'groups'],
    queryFn: () => api.get<AssignmentGroupRecord[]>(`${BASE}/groups`),
  });
}

export function useRoutingRules() {
  return useQuery({
    queryKey: [...KEY, 'routing-rules'],
    queryFn: () => api.get<RoutingRuleRecord[]>(`${BASE}/routing-rules`),
  });
}

export function useUpsertRoutingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RoutingRuleRecord) => api.post<RoutingRuleRecord>(`${BASE}/routing-rules`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'routing-rules'] }),
  });
}

export function useDeleteRoutingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<boolean>(`${BASE}/routing-rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'routing-rules'] }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignmentGroupRecord) => api.post<AssignmentGroupRecord>(`${BASE}/groups`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useOnCallShifts() {
  return useQuery({
    queryKey: [...KEY, 'on-call'],
    queryFn: () => api.get<OnCallShiftRecord[]>(`${BASE}/on-call`),
  });
}

export function useUpsertOnCallShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OnCallShiftRecord) => api.post<OnCallShiftRecord>(`${BASE}/on-call`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'on-call'] }),
  });
}

export function useDeleteOnCallShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<boolean>(`${BASE}/on-call/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'on-call'] }),
  });
}

export function useChangeTemplates() {
  return useQuery({
    queryKey: [...KEY, 'templates'],
    queryFn: () => api.get<ChangeTemplateRecord[]>(`${BASE}/change-templates`),
  });
}

export function useUpsertChangeTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ChangeTemplateRecord) =>
      api.post<ChangeTemplateRecord>(`${BASE}/change-templates`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useKbArticles() {
  return useQuery({
    queryKey: [...KEY, 'kb'],
    queryFn: () => api.get<KbArticleRecord[]>(`${BASE}/kb`),
  });
}

export function useUpsertKbArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: KbArticleRecord) => api.post<string>(`${BASE}/kb`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useConfirmKbArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<boolean>(`${BASE}/kb/${id}/confirm`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCommonsClasses() {
  return useQuery({
    queryKey: [...KEY, 'classes'],
    queryFn: () => api.get<AssetClassRecord[]>(`${BASE}/classes`),
  });
}

/** E.2 (CI Class Manager): create/update a taxonomy class (full-row upsert by code).
 *
 *  ⚠ Two kinds of field, two rules. `min_stock` and the naming/accounting columns
 *  are a FULL-ROW replace — omit one and it clears. The R-234/R-235 authoring
 *  fields (`domains`, `tracking`, `tag_prefix`, `asset_kind`,
 *  `is_connected_device`, `expects_*`) are the opposite: OMITTED MEANS LEAVE, so a
 *  form that did not collect them cannot un-narrow a class by saving a rename. */
export function useUpsertClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssetClassRecord) => api.post<AssetClassRecord>(`${BASE}/classes`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Shelve / un-shelve a class — its own endpoint, and it has to be.
 *
 *  ⚠ NOT a field on the upsert. `upsert_class` is also the CSV taxonomy re-import
 *  path, and that import passes a hardcoded `active = true` for every row it reads,
 *  so while `active` rode the upsert one re-import silently un-shelved every class
 *  an operator had retired. The server now leaves `active` alone on conflict and
 *  writes it only through here.
 *
 *  Shelving is the only way to take a class out of circulation: the delete refusal
 *  counts RETIRED units and a unit is never deleted, so a class that has ever held
 *  one is undeletable for ever (faithful to the prototype — its `assetsOfClass` has
 *  no state filter either, and it has no delete-an-asset action at all). */
export function useSetClassActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, active }: { code: string; active: boolean }) =>
      api.post<AssetClassRecord>(`${BASE}/classes/${encodeURIComponent(code)}/active`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-235: what blocks deleting a class — ask BEFORE offering the button.
 *
 *  Ten possible dependants, six of which the prototype knows nothing about; two
 *  of those cascade, so this is the difference between a refusal and silently
 *  deleting somebody's PM plans. */
export function useClassDependants(code: string | null) {
  return useQuery({
    queryKey: [...KEY, 'class-dependants', code],
    queryFn: () => api.get<ClassDependants>(`${BASE}/classes/${encodeURIComponent(code ?? '')}/dependants`),
    enabled: !!code,
  });
}

export function useDeleteClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.delete<boolean>(`${BASE}/classes/${encodeURIComponent(code)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-235: delete one attribute definition.
 *
 *  ⚠ Narrower than it looks, and the UI must say so. Values already stored against
 *  the key live inside each asset's `attributes` JSONB at the sites, with no FK to
 *  count — so removing the def does not remove them, it makes site validation
 *  reject the key as unknown and every asset of that class unsaveable until the
 *  JSONB is cleaned. It is for a def created by mistake, not for pruning. */
export function useDeleteAttributeDef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ class_code, key }: { class_code: string; key: string }) =>
      api.delete<boolean>(
        `${BASE}/attribute-defs/${encodeURIComponent(class_code)}/${encodeURIComponent(key)}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCommonsAttributeDefs(classCode: string | null) {
  return useQuery({
    queryKey: [...KEY, 'attribute-defs', classCode],
    queryFn: () =>
      api.get<AttributeDefRecord[]>(`${BASE}/attribute-defs?class=${encodeURIComponent(classCode ?? '')}`),
    enabled: !!classCode,
  });
}

/** R-235: author one attribute definition. Upsert by (class_code, key), so
 *  editing a row and adding one are the same call — and `lives_on` rides it, which
 *  is what splits the asset record's attributes tab and decides which fields
 *  intake asks for at the dock. */
export function useUpsertAttributeDef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AttributeDefRecord) => api.post<AttributeDefRecord>(`${BASE}/attribute-defs`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'attribute-defs'] }),
  });
}

export function useCommonsPmCatalog(classCode: string | null) {
  return useQuery({
    queryKey: [...KEY, 'pm-catalog', classCode],
    queryFn: () =>
      api.get<PmCatalogRecord[]>(`${BASE}/pm-catalog?class=${encodeURIComponent(classCode ?? '')}`),
    enabled: !!classCode,
  });
}

export function useCommonsLocationTokens() {
  return useQuery({
    queryKey: [...KEY, 'location-tokens'],
    queryFn: () => api.get<LocationTokenRecord[]>(`${BASE}/location-tokens`),
  });
}

export function useImportCommonsCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entity, csv, dryRun }: { entity: CommonsImportEntity; csv: string; dryRun: boolean }) =>
      api.post<CommonsImportReport>(`${BASE}/import?entity=${entity}&dry_run=${dryRun}`, csv, {
        headers: { 'Content-Type': 'text/csv' },
      }),
    onSuccess: (_r, vars) => {
      if (!vars.dryRun) qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useKpiBenchmarks() {
  return useQuery({
    queryKey: [...KEY, 'benchmarks'],
    queryFn: () => api.get<KpiBenchmarkRecord[]>(`${BASE}/kpi-benchmarks`),
  });
}

// ── asset models (R-165 / D-120) ──────────────────────────────────────────────

/** Live canonical product-catalogue rows (superseded ones are excluded server-side). */
export function useCommonsModels() {
  return useQuery({
    queryKey: [...KEY, 'models'],
    queryFn: () => api.get<AssetModelRecord[]>(`${BASE}/models`),
  });
}

/** The steward queue — promoted-but-unreviewed rows. Non-blocking by design:
 *  everything listed here is already usable at every site. */
export function useUnreviewedModels() {
  return useQuery({
    queryKey: [...KEY, 'models', 'unreviewed'],
    queryFn: () => api.get<AssetModelRecord[]>(`${BASE}/models/unreviewed`),
  });
}

export function useUpsertModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssetModelRecord) => api.post<AssetModelRecord>(`${BASE}/models`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'models'] }),
  });
}

/** Merge a near-duplicate into the row that survives. The loser is superseded
 *  and aliased, NEVER deleted, so assets pointing at it keep resolving. */
export function useMergeModels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MergeModelsBody) => api.post<AssetModelRecord>(`${BASE}/models/merge`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'models'] }),
  });
}

/** R-235: delete a product — only when nothing points at it and it is in no alias
 *  chain. Anything with history is MERGED instead (see `useMergeModels`), because
 *  a hard delete inside a chain strands the assets resolving through it. The
 *  server answers 409 with that advice in the message. */
export function useDeleteModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<boolean>(`${BASE}/models/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'models'] }),
  });
}

export function useApproveModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<AssetModelRecord>(`${BASE}/models/${id}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'models'] }),
  });
}
